// Story 6.16 — per-user (and platform-wide) ledger export download.
//
// There is no query cache here: an export is a file the admin pulls on demand
// (the story's recommendation — nothing stored, the ledger is the record). The
// hook fetches the CSV as a blob and triggers a browser download. Auth is the
// admin session cookie (`credentials: "include"`), same as every admin call.

import { attachRequestIdToScope } from "../../lib/api-client";

export type LedgerExportScope =
  | { kind: "user"; userId: string }
  | { kind: "platform" };

export type LedgerExportParams = {
  scope: LedgerExportScope;
  from: string; // YYYY-MM-DD, inclusive
  to: string; // YYYY-MM-DD, inclusive
  /** Danish Excel defaults to `;`; comma (RFC 4180) is the default here. */
  delimiter?: "comma" | "semicolon";
};

function buildUrl(params: LedgerExportParams): string {
  const qs = new URLSearchParams();
  qs.set("from", params.from);
  qs.set("to", params.to);
  qs.set("format", "csv");
  if (params.delimiter === "semicolon") {
    qs.set("delimiter", "semicolon");
  }
  const base =
    params.scope.kind === "user"
      ? `/api/v1/admin/users/${params.scope.userId}/ledger-export`
      : `/api/v1/admin/ledger-export`;
  return `${base}?${qs.toString()}`;
}

function filenameFromDisposition(disposition: string | null, fallback: string): string {
  if (!disposition) return fallback;
  const match = /filename="?([^"]+)"?/i.exec(disposition);
  return match?.[1] ?? fallback;
}

/**
 * Fetch the CSV and hand the browser a download. Returns the row count from the
 * `x-row-count` header for a confirmation toast. Throws on a non-2xx response.
 */
export async function downloadLedgerExport(params: LedgerExportParams): Promise<{ rowCount: number | null }> {
  const url = buildUrl(params);
  const response = await fetch(url, { credentials: "include" });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Ledger export failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      /* body was not JSON (or was the CSV) */
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition");
  const fallback =
    params.scope.kind === "user"
      ? `greenhold-ledger-${params.scope.userId}-${params.from}-to-${params.to}.csv`
      : `greenhold-ledger-platform-${params.from}-to-${params.to}.csv`;
  const filename = filenameFromDisposition(disposition, fallback);

  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);

  const rowCountHeader = response.headers.get("x-row-count");
  const rowCount = rowCountHeader ? Number.parseInt(rowCountHeader, 10) : null;
  return { rowCount: Number.isNaN(rowCount ?? NaN) ? null : rowCount };
}
